import React, { FC, useCallback, useMemo, useState } from "react";

import { useWindowSize } from "~/hooks/useWindowSize";

import { type SectionsProps } from "~/components/Sections/withSections";

import { ComponentProps } from "~/types/components";

// @todo When SSR is the only target, import the proper Sanity collection type and get rid of this.
export type CollectionTypes = {
  content: SectionsProps[];
  footerContent: SectionsProps[];
  descriptionHtml: string;
  shopify: {
    handle?: string;
    shopifyHandle?: string;
    id: string;
  };
  title: string;
  primaryCollection?: {
    reference: {
      title: string;
      handle: string;
    };
  };
};

type CollectionInputProps = ComponentProps & {
  collection: CollectionTypes;
  collectionGender: string | null;
};

type CollectionOutputProps = Pick<ComponentProps, "location"> & {
  collection: CollectionTypes;
  description: string;
  handle?: string;
  hideBreadcrumbAndTitle: boolean;
  paths: {
    title: string | null;
    url?: string | null;
  }[];
  title: string;
  filterBarRef: (node: HTMLDivElement) => (() => void) | null | undefined;
  filterBarStuck: boolean;
  header: SectionsProps[];
  footer: SectionsProps[];
  collectionHeroBannerContent?: SectionsProps;
  primaryCollection?: {
    reference?: {
      id: string;
      title: string;
      handle: string;
    };
  };
};

export const withCollection =
  (Component: FC<CollectionOutputProps>) =>
  ({
    name = "Collection",
    collection,
    collectionGender,
  }: CollectionInputProps) => {
    const [filterBarStuck, setFilterBarStuck] = useState(false);
    const {
      windowSize: { isMD },
    } = useWindowSize();

    const {
      shopify,
      title,
      descriptionHtml,
      content,
      footerContent,
      primaryCollection,
    } = collection;
    const hideBreadcrumbAndTitle: boolean =
      content?.find((item) => item._type === "sectionCollectionHeroBanner")
        ?.hideBreadcrumbAndTitle || false;
    const collectionHeroBannerContent = content?.find(
      (item) => item._type === "sectionCollectionHeroBanner",
    );

    const paths = useMemo<CollectionOutputProps["paths"]>(() => {
      if (hideBreadcrumbAndTitle) {
        return [];
      }

      const genderLink: CollectionOutputProps["paths"][0] = {
        title:
          collectionGender && collectionGender != "Accessories"
            ? collectionGender
            : "Home",
        url:
          collectionGender && collectionGender != "Accessories"
            ? `/collections/${collectionGender.includes("Women") ? "all-womens" : "all-mens"}`
            : "/",
      };

      const primaryCollectionLink = primaryCollection?.reference
        ? {
            title: primaryCollection.reference.title.replace(
              /(Women's|Men's)\s*/g,
              "",
            ),
            url: `/collections/${primaryCollection.reference.handle}`,
          }
        : null;

      const pathArray = [genderLink];
      if (primaryCollectionLink) pathArray.push(primaryCollectionLink);

      // Add current collection as final item without URL
      pathArray.push({
        title: title,
      });

      return pathArray;
    }, [
      collectionGender,
      primaryCollection,
      hideBreadcrumbAndTitle,
      title,
      shopify?.handle,
    ]);

    const filterBarRef = useCallback(
      (node: HTMLDivElement) => {
        if (!window.IntersectionObserver) {
          return null;
        }
        const observer = new IntersectionObserver(
          ([e]) => {
            if (!e) return;

            setFilterBarStuck(1 > e.intersectionRatio);
          },
          {
            threshold: 1,
            rootMargin: `${isMD ? "-125px 0px 0px 0px" : "-90px 0px 0px 0px"}`,
          },
        );
        if (!node) {
          return;
        }
        observer.observe(node);

        return () => observer.disconnect();
      },
      [isMD],
    );

    // In static, shopify.shopifyHandle is rewritten to shopify.handle.
    // @todo This is not a problem in SSR, use the proper Sanity type when SSR is only target.
    const handle = shopify?.handle ?? shopify?.shopifyHandle;

    Component.displayName = name;
    return (
      <Component
        collection={collection}
        description={descriptionHtml}
        filterBarRef={filterBarRef}
        filterBarStuck={filterBarStuck}
        handle={handle}
        hideBreadcrumbAndTitle={hideBreadcrumbAndTitle}
        paths={paths}
        title={title}
        header={content}
        footer={footerContent}
        collectionHeroBannerContent={collectionHeroBannerContent}
      />
    );
  };
